import { BaseApiService, ApiResponse } from './base-api';
import { API_CONFIG } from '../config/api';
import { auth0PasskeyService } from './auth0-passkey.service';
import { auth0BiometricService } from './auth0-biometric.service';
import {
  Auth0MagicLinkSend,
  Auth0MagicLinkSendResponse,
  Auth0MagicLinkVerify,
  Auth0MagicLinkVerifyResponse,
  Auth0SessionVerify,
  Auth0SessionVerifyResponse,
  Auth0User,
  AuthMethod,
  AuthMethodCapabilities,
  PasskeyCredential,
  BiometricStatus,
  PasskeyRegistrationStart,
  PasskeyRegistrationComplete,
  BiometricEnrollmentStart,
  BiometricStatusUpdate,
  PasskeyPreferences,
  PasskeySetupRecommendation,
  PasskeySetupResponse
} from '../types/auth';
import apiClient from './api';
import { setSessionCookie } from '../utils/sessionUtils';

// Legacy Authentication types (for backward compatibility)
export interface MagicLinkRequest {
  email: string;
  tenantId?: string;
  hotelId?: string;
  redirectUrl?: string;
  hotelSlug?: string;
  hotelSubDomain?: string;
  hotelName?: string;
  domain?: string;
  address?: string;
  city?: string;
  country?: string;
  zip?: string;
  phone?: string;
  contactEmail?: string;
}

export interface MagicLinkResponse {
  success: boolean;
  message: string;
  linkId: string;
  expiresAt: string;
}

export interface VerifyMagicLinkRequest {
  linkId: string;
  token: string;
  email: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface GuestProfile {
  id: string;
  email: string;
  fullName?: string;
  phone?: string;
  operaProfileId?: string;
  preferences: {
    language?: string;
    currency?: string;
    communicationPreference?: 'email' | 'sms' | 'both';
  };
  journeyStage: 'prospect' | 'pre-stay' | 'in-stay' | 'post-stay';
  currentReservation?: {
    confirmationNumber: string;
    checkInDate: string;
    checkOutDate: string;
    roomType: string;
    hotelCode: string;
  };
  lastStayDate?: string;
  isVIP: boolean;
  loyaltyTier?: string;
}

export interface AuthenticatedUser {
  tokens: AuthTokens;
  profile: GuestProfile;
  permissions: string[];
}

export class AuthService extends BaseApiService {
  public passkeyService = auth0PasskeyService;
  public biometricService = auth0BiometricService;

  constructor() {
    super(API_CONFIG.baseURL);
  }

  // =============================================================================
  // AUTH0 AUTHENTICATION METHODS
  // =============================================================================

  /**
   * Check authentication method capabilities for the current device
   */
  async getAuthCapabilities(): Promise<AuthMethodCapabilities> {
    console.log('🔍 Checking authentication capabilities...');
    
    const capabilities: AuthMethodCapabilities = {
      passkey: {
        supported: this.passkeyService.isWebAuthnSupported(),
        available: await this.passkeyService.isPlatformAuthenticatorSupported()
      },
      biometric: {
        supported: false,
        available: false,
        types: []
      },
      magicLink: {
        supported: true,
        available: true
      }
    };

    // Check biometric capabilities
    const biometricSupport = await this.biometricService.checkBiometricSupport();
    capabilities.biometric = {
      supported: biometricSupport.supported,
      available: biometricSupport.supported,
      types: biometricSupport.availableTypes
    };

    console.log('✅ Authentication capabilities:', capabilities);
    return capabilities;
  }

  // =============================================================================
  // PASSKEY AUTHENTICATION (No Session Required)
  // =============================================================================

  /**
   * Start passkey authentication for login (no session required)
   */
  async startPasskeyAuthentication(hotelId: string, email?: string): Promise<{
    success: boolean;
    challenge?: string;
    sessionId?: string;
    publicKey?: PublicKeyCredentialRequestOptions;
    error?: string;
  }> {
    try {
      console.log('🔐 Starting passkey authentication for hotel:', hotelId, email ? `email: ${email}` : '(no email)');
      
      const requestData: { hotel_id: string; email?: string } = {
        hotel_id: hotelId
      };
      
      // Add email if provided (optional for passkey auth)
      if (email) {
        requestData.email = email;
      }
      
      const response = await apiClient.post<{
        challenge: string;
        sessionId: string;
        publicKey: PublicKeyCredentialRequestOptions;
      }>('/auth0/passkey/authenticate/start', requestData);
      
      if (response.success) {
        console.log('📥 Raw authentication response:', response);
        
        // Handle potential nested response format
        const responseData = (response as any).authentication_data || response;
        
        console.log('📥 Processed authentication challenge:', {
          challenge: responseData.challenge,
          publicKey: {
            ...responseData.publicKey,
            challenge: typeof responseData.publicKey?.challenge,
            allowCredentials: responseData.publicKey?.allowCredentials?.length
          }
        });
        
        return {
          success: true,
          challenge: responseData.challenge,
          sessionId: responseData.sessionId,
          publicKey: responseData.publicKey
        };
      }
      
      return {
        success: false,
        error: typeof response.error === 'string' ? response.error : response.error?.message || 'Failed to start passkey authentication'
      };
    } catch (error) {
      console.error('❌ Failed to start passkey authentication:', error);
      return {
        success: false,
        error: 'Failed to start passkey authentication'
      };
    }
  }

  /**
   * Complete passkey authentication for login (no session required)
   */
  async completePasskeyAuthentication(
    credential: PublicKeyCredential,
    sessionId?: string
  ): Promise<PasskeySetupResponse> {
    try {
      console.log('🔐 Completing passkey authentication...');
      
      // Serialize the credential properly for authentication (different from registration)
      const credentialData = {
        id: credential.id,
        rawId: this.arrayBufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          clientDataJSON: this.arrayBufferToBase64(credential.response.clientDataJSON),
          authenticatorData: this.arrayBufferToBase64(
            (credential.response as AuthenticatorAssertionResponse).authenticatorData
          ),
          signature: this.arrayBufferToBase64(
            (credential.response as AuthenticatorAssertionResponse).signature
          ),
          userHandle: (credential.response as AuthenticatorAssertionResponse).userHandle 
            ? this.arrayBufferToBase64((credential.response as AuthenticatorAssertionResponse).userHandle!)
            : null
        }
      };

      console.log('📤 Sending authentication credential:', {
        id: credentialData.id,
        type: credentialData.type,
        rawId: `base64(${credentialData.rawId.length} chars)`,
        response: {
          clientDataJSON: `base64(${credentialData.response.clientDataJSON.length} chars)`,
          authenticatorData: `base64(${credentialData.response.authenticatorData.length} chars)`,
          signature: `base64(${credentialData.response.signature.length} chars)`,
          userHandle: credentialData.response.userHandle ? `base64(${credentialData.response.userHandle.length} chars)` : 'null'
        }
      });
      
      // Let's also decode and check the clientDataJSON to verify challenge
      try {
        const clientDataJSON = JSON.parse(atob(credentialData.response.clientDataJSON));
        console.log('🔍 ClientDataJSON challenge:', clientDataJSON.challenge);
        console.log('🔍 ClientDataJSON origin:', clientDataJSON.origin);
        console.log('🔍 ClientDataJSON type:', clientDataJSON.type);
      } catch (e) {
        console.warn('⚠️ Could not decode clientDataJSON:', e);
      }
      
      const response = await this.post<{
        user: any;
        accessToken?: string;
        refreshToken?: string;
        idToken?: string;
        expiresIn?: number;
        session_token?: string;
        userExists: boolean;
        passkey_setup?: PasskeySetupRecommendation;
        hotel_id?: string;
        guestId?: string;
        message?: string;
        authentication_method?: string;
      }>('/auth0/passkey/authenticate/complete', { 
        session_id: sessionId || '', // Use session ID from start authentication
        credential_response: credentialData 
      });
      
      if (response.success && response.data) {
        // Store session token in cookie (like magic link flow)
        const sessionToken = response.data.session_token;
        if (sessionToken) {
          setSessionCookie(sessionToken);
          console.log('🍪 Session token set in cookie for passkey authentication');
          
          // Update app session state directly
          if ((window as any).setSessionValid) {
            console.log('✅ Setting session as valid directly...');
            (window as any).setSessionValid();
          }
        }
        
        // Store Auth0 tokens if available
        if (response.data.accessToken) {
          this.setAuthTokens(
            response.data.accessToken,
            response.data.refreshToken || '',
            response.data.expiresIn || 3600
          );
        }
        
        return {
          success: true,
          user: response.data.user,
          userExists: response.data.userExists,
          passkey_setup: response.data.passkey_setup,
          tokens: {
            accessToken: response.data.accessToken,
            idToken: response.data.idToken,
            refreshToken: response.data.refreshToken,
            expiresIn: response.data.expiresIn,
            sessionToken: sessionToken
          }
        };
      }
      
      return {
        success: false,
        error: response.error || 'Failed to complete passkey authentication'
      };
    } catch (error) {
      console.error('❌ Failed to complete passkey authentication:', error);
      return {
        success: false,
        error: 'Failed to complete passkey authentication'
      };
    }
  }

  /**
   * Simplified passkey authentication flow (no session required)
   */
  async authenticateWithPasskey(hotelId: string, email?: string): Promise<PasskeySetupResponse> {
    try {
      // Step 1: Start authentication
      const startResult = await this.startPasskeyAuthentication(hotelId, email);
      if (!startResult.success || !startResult.publicKey) {
        return {
          success: false,
          error: startResult.error || 'Failed to start passkey authentication'
        };
      }

      // Step 2: Convert base64url challenge to ArrayBuffer for WebAuthn API
      const currentDomain = window.location.hostname;
      const backendRpId = startResult.publicKey.rpId;
      
      console.log('🌐 Current domain:', currentDomain);
      console.log('🏢 Backend RP ID:', backendRpId);
      
      // Always use current domain for RP ID (WebAuthn security requirement)
      if (backendRpId && backendRpId !== currentDomain && !currentDomain.endsWith('.' + backendRpId)) {
        console.warn('⚠️ RP ID mismatch detected for authentication!');
        console.warn('Current domain:', currentDomain);
        console.warn('Backend RP ID:', backendRpId);
        console.warn('Using current domain as RP ID (WebAuthn requirement)');
        
        // Check if we're on localhost with different RP ID
        if (currentDomain === 'localhost' || currentDomain.startsWith('127.0.0.1')) {
          console.warn('🔧 Development mode: Using localhost as RP ID');
          console.warn('💡 If you have no passkeys for localhost yet, authenticate with email first, then set up a new passkey from the dashboard');
        }
      }
      
      const publicKey = {
        ...startResult.publicKey,
        challenge: this.base64urlToArrayBuffer(startResult.publicKey.challenge as unknown as string),
        rpId: currentDomain,
        allowCredentials: startResult.publicKey.allowCredentials?.map(cred => ({
          ...cred,
          id: this.base64urlToArrayBuffer(cred.id as unknown as string)
        }))
      };

      console.log('🔄 Converted authentication publicKey for WebAuthn:', {
        ...publicKey,
        challenge: `ArrayBuffer(${publicKey.challenge.byteLength} bytes)`,
        rpId: publicKey.rpId,
        allowCredentials: publicKey.allowCredentials?.map(cred => ({
          ...cred,
          id: `ArrayBuffer(${cred.id.byteLength} bytes)`,
          transports: cred.transports
        }))
      });
      
      console.log('📋 Available credentials count:', publicKey.allowCredentials?.length || 0);
      console.log('🔐 User verification:', publicKey.userVerification);

      // Step 3: Use WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: publicKey
      }) as PublicKeyCredential;

      if (!credential) {
        return {
          success: false,
          error: 'Passkey authentication was cancelled'
        };
      }

      console.log('🔐 WebAuthn credential created:', {
        id: credential.id,
        type: credential.type,
        rawId: `ArrayBuffer(${credential.rawId.byteLength} bytes)`,
        response: {
          clientDataJSON: `ArrayBuffer(${credential.response.clientDataJSON.byteLength} bytes)`,
          authenticatorData: `ArrayBuffer(${(credential.response as AuthenticatorAssertionResponse).authenticatorData.byteLength} bytes)`,
          signature: `ArrayBuffer(${(credential.response as AuthenticatorAssertionResponse).signature.byteLength} bytes)`,
          userHandle: (credential.response as AuthenticatorAssertionResponse).userHandle 
            ? `ArrayBuffer(${(credential.response as AuthenticatorAssertionResponse).userHandle!.byteLength} bytes)`
            : 'null'
        }
      });

      // Step 4: Complete authentication
      const completeResult = await this.completePasskeyAuthentication(credential, startResult.sessionId);
      
      return {
        success: completeResult.success,
        user: completeResult.user,
        userExists: completeResult.userExists,
        passkey_setup: completeResult.passkey_setup,
        error: completeResult.error
      };
    } catch (error: any) {
      console.error('❌ Passkey authentication failed:', error);
      
      // Handle specific WebAuthn errors
      if (error.name === 'NotAllowedError') {
        return {
          success: false,
          error: 'Passkey authentication was cancelled or failed'
        };
      }
      
      if (error.name === 'SecurityError' && error.message.includes('relying party ID')) {
        return {
          success: false,
          error: 'Passkey not available for this domain. Please use email authentication.'
        };
      }
      
      if (error.name === 'NotSupportedError') {
        return {
          success: false,
          error: 'Passkey authentication is not supported on this device.'
        };
      }
      
      // Generic fallback error
      return {
        success: false,
        error: error.message || 'Passkey authentication failed'
      };
    }
  }

  // =============================================================================
  // PASSKEY REGISTRATION (Session Required - for setup after login)
  // =============================================================================

  /**
   * Start passkey registration process
   */
  async startPasskeyRegistration(email: string, hotelId: string): Promise<{
    success: boolean;
    challenge?: string;
    sessionId?: string;
    publicKey?: PublicKeyCredentialCreationOptions;
    enrollment_url?: string;
    user_id?: string;
    error?: string;
  }> {
    try {
      const response = await this.passkeyService.startPasskeyRegistration({ email, hotel_id: hotelId }) as any; // Adjust type as needed
      
      if (response.success) {
        // Handle Auth0 hosted enrollment flow
        if (response.enrollment_url) {
          return {
            success: true,
            enrollment_url: response.enrollment_url,
            user_id: response.user_id
          };
        }
        
        // Handle traditional WebAuthn flow
        return {
          success: true,
          challenge: response.challenge,
          sessionId: response.sessionId,
          publicKey: response.publicKey
        };
      }
      
      return {
        success: false,
        error: response.error || 'Failed to start passkey registration'
      };
    } catch (error) {
      console.error('❌ Failed to start passkey registration:', error);
      return {
        success: false,
        error: 'Failed to start passkey registration'
      };
    }
  }

  /**
   * Complete passkey registration process
   */
  async completePasskeyRegistration(
    sessionId: string,
    credential: PublicKeyCredential,
    name?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.passkeyService.completePasskeyRegistration({
        sessionId,
        credential,
        name
      });
      
      return {
        success: response.success,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to complete passkey registration:', error);
      return {
        success: false,
        error: 'Failed to complete passkey registration'
      };
    }
  }

  /**
   * Get user's passkeys
   */
  async getUserPasskeys(): Promise<{ success: boolean; passkeys?: PasskeyCredential[]; error?: string }> {
    try {
      const response = await this.passkeyService.listPasskeys();
      
      return {
        success: response.success,
        passkeys: response.data,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to get user passkeys:', error);
      return {
        success: false,
        error: 'Failed to retrieve passkeys'
      };
    }
  }

  /**
   * Delete a passkey
   */
  async deletePasskey(credentialId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.passkeyService.deletePasskey(credentialId);
      
      return {
        success: response.success,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to delete passkey:', error);
      return {
        success: false,
        error: 'Failed to delete passkey'
      };
    }
  }

  // =============================================================================
  // BIOMETRIC AUTHENTICATION
  // =============================================================================

  /**
   * Start biometric enrollment
   */
  async startBiometricEnrollment(
    email: string,
    biometricType: string
  ): Promise<{ success: boolean; enrollmentId?: string; error?: string }> {
    try {
      const response = await this.biometricService.startBiometricEnrollment({
        email,
        biometricType: biometricType as any
      });
      
      return {
        success: response.success,
        enrollmentId: response.data?.enrollmentId,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to start biometric enrollment:', error);
      return {
        success: false,
        error: 'Failed to start biometric enrollment'
      };
    }
  }

  /**
   * Get biometric status
   */
  async getBiometricStatus(): Promise<{ success: boolean; status?: BiometricStatus; error?: string }> {
    try {
      const response = await this.biometricService.getBiometricStatus();
      
      return {
        success: response.success,
        status: response.data,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to get biometric status:', error);
      return {
        success: false,
        error: 'Failed to get biometric status'
      };
    }
  }

  /**
   * Update biometric status
   */
  async updateBiometricStatus(
    biometricType: string,
    enabled: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.biometricService.updateBiometricStatus({
        biometricType,
        enabled
      });
      
      return {
        success: response.success,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to update biometric status:', error);
      return {
        success: false,
        error: 'Failed to update biometric status'
      };
    }
  }

  // =============================================================================
  // PASSKEY PREFERENCE MANAGEMENT
  // =============================================================================

  /**
   * Update user's passkey preferences in Auth0 metadata
   */
  async updatePasskeyPreferences(
    action: 'skip_temporarily' | 'skip_permanently' | 'completed'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('🔐 Updating passkey preferences:', action);
      
      const response = await this.post<{ success: boolean }>(
        '/auth0/passkey/preferences/update',
        { action },
        { requireAuth: true }
      );
      
      return {
        success: response.success,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to update passkey preferences:', error);
      return {
        success: false,
        error: 'Failed to update passkey preferences'
      };
    }
  }

  /**
   * Get user's current passkey preferences from Auth0 metadata
   */
  async getPasskeyPreferences(): Promise<{ 
    success: boolean; 
    preferences?: PasskeyPreferences; 
    error?: string; 
  }> {
    try {
      const response = await this.get<PasskeyPreferences>(
        '/auth0/passkey/preferences',
        { requireAuth: true }
      );
      
      return {
        success: response.success,
        preferences: response.data,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to get passkey preferences:', error);
      return {
        success: false,
        error: 'Failed to get passkey preferences'
      };
    }
  }

  /**
   * Get intelligent passkey setup recommendations
   */
  async getPasskeySetupRecommendations(): Promise<{
    success: boolean;
    recommendation?: PasskeySetupRecommendation;
    error?: string;
  }> {
    try {
      const response = await this.get<PasskeySetupRecommendation>(
        '/auth0/passkey/setup/recommendations',
        { requireAuth: true }
      );
      
      return {
        success: response.success,
        recommendation: response.data,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to get passkey setup recommendations:', error);
      return {
        success: false,
        error: 'Failed to get setup recommendations'
      };
    }
  }

  // =============================================================================
  // AUTH0 MAGIC LINK (FALLBACK)
  // =============================================================================

  /**
   * Send Auth0 magic link
   */
  async sendAuth0MagicLink(email: string, hotelId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.post<Auth0MagicLinkSendResponse>(
        '/auth0/passwordless/email/send',
        { email, hotel_id: hotelId }
      );

      return {
        success: response.success,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to send Auth0 magic link:', error);
      return {
        success: false,
        error: 'Failed to send magic link'
      };
    }
  }

  /**
   * Send a 6-digit verification code to the guest's email.
   * Backend: POST /auth0/passwordless/email/send-code. Reissuing invalidates
   * any previous code for the same (email, hotelId) pair. Use this for
   * "Resend code" too — there's no separate resend endpoint.
   *
   * Uses `apiClient` (axios, withCredentials=true) so the request matches the
   * magic-link path and any `Set-Cookie` headers from backend are honoured.
   * BaseApiService.post uses raw fetch without credentials, which would drop
   * cookies on the floor — verifyVerificationCode strictly requires them.
   */
  async sendVerificationCode(
    email: string,
    hotelId: string,
  ): Promise<{ success: boolean; error?: string; message?: string; email?: string }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        error?: string;
        message?: string;
        email?: string;
      }>('/auth0/passwordless/email/send-code', {
        email,
        hotel_id: hotelId,
      });

      // apiClient returns ApiResponse<T> where the backend body sits under
      // `data` unless the body itself has a `success` key — in which case
      // formatResponse passes it through as-is. Try both shapes.
      const body: any = (response as any)?.data ?? response;
      return {
        success: !!body?.success,
        error: body?.error || (response as any)?.error,
        message: body?.message,
        email: body?.email,
      };
    } catch (error) {
      console.error('❌ Failed to send verification code:', error);
      return {
        success: false,
        error: 'Failed to send verification code',
      };
    }
  }

  /**
   * Verify a 6-digit code the guest typed. Same response shape as the
   * magic-link /verify endpoint — see MagicLinkVerifyResponse in
   * MagicLinkVerifyPage.tsx for the full union (success / trust-selection /
   * isFirstTimeGuest branches).
   *
   * Uses `apiClient` (axios, withCredentials=true) so backend's
   *   Set-Cookie: session=<jwt>; HttpOnly; ...
   *   Set-Cookie: session_token=<jwt>; ...
   * headers are stored by the browser exactly like the magic-link /verify call.
   *
   * Backend may include `reason` on validation failures:
   *   mismatch          — wrong code (attempts incremented)
   *   not_found         — code expired or never sent
   *   too_many_attempts — 5+ wrong attempts, code invalidated
   *   redis_error       — verification service temporarily unavailable
   * UI surfaces the `error` string directly (already user-facing).
   */
  async verifyVerificationCode(
    code: string,
    email: string,
    hotelId: string,
    deviceDetectionId?: string,
  ): Promise<any> {
    const payload: Record<string, string> = {
      code,
      email,
      hotel_id: hotelId,
    };
    if (deviceDetectionId) payload.device_detection_id = deviceDetectionId;

    // apiClient (axios + withCredentials). formatResponse passes the body
    // through if it has `success` at the top level; otherwise wraps. The
    // backend's /verify-code response always carries `success` at top level,
    // so the returned object IS the backend body — same as the magic-link
    // verify path in MagicLinkVerifyPage.
    const response = await apiClient.post<any>(
      '/auth0/passwordless/email/verify-code',
      payload,
    );
    return response;
  }

  /**
   * Start Google OAuth flow.
   *
   * Backend: POST /auth0/google/auth/start with { hotel_id }. Backend
   * constructs the Google consent URL (with state = base64(hotel_id)) and
   * returns it. FE redirects the top-level window to that URL — Google
   * blocks OAuth flows inside iframes, so any embedding marketing page must
   * be escaped via window.top.location.href.
   *
   * The `redirect_uri` field is intentionally NOT sent — backend uses its
   * AUTH0_CALLBACK_URL env var, which points at the marketing-site
   * redirector (zenpacorlando/auth0/callback.html → app.zenpacorlando.com/
   * auth0/callback).
   */
  async startGoogleAuth(
    hotelId: string,
  ): Promise<{ success: boolean; auth_url?: string; error?: string }> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        auth_url?: string;
        error?: string;
      }>('/auth0/google/auth/start', { hotel_id: hotelId });

      const body: any = (response as any)?.data ?? response;
      return {
        success: !!body?.success,
        auth_url: body?.auth_url,
        error: body?.error || (response as any)?.error,
      };
    } catch (error) {
      console.error('❌ Failed to start Google auth:', error);
      return {
        success: false,
        error: 'Failed to start Google sign-in',
      };
    }
  }

  /**
   * Complete the Google OAuth flow by exchanging the code + state for a
   * session. Backend: POST /auth0/google/auth/callback. Same response shape
   * as /auth0/passwordless/email/verify-code (see MagicLinkVerifyResponse /
   * OTPVerifyResponse) — success, user, session_token, userExists,
   * passkey_setup, requires_trust_selection branch, etc.
   *
   * Uses apiClient (axios + withCredentials: true) so the backend's
   *   Set-Cookie: session=<jwt>; Domain=.zenpacorlando.com; HttpOnly; ...
   *   Set-Cookie: session_token=<jwt>; Domain=.zenpacorlando.com; ...
   * headers are stored by the browser. The HttpOnly `session` cookie is
   * what subsequent /api/* calls authenticate against; without credentials
   * the browser would drop both.
   *
   * `state` is opaque to the FE — pass through whatever Google returned to
   * the redirector. Backend decodes it to recover the original hotel_id.
   */
  async completeGoogleAuth(
    code: string,
    state: string,
    deviceDetectionId?: string,
  ): Promise<any> {
    const payload: Record<string, string> = { code, state };
    if (deviceDetectionId) payload.device_detection_id = deviceDetectionId;

    const response = await apiClient.post<any>(
      '/auth0/google/auth/callback',
      payload,
    );
    return response;
  }

  /**
   * Verify Auth0 magic link
   */
  async verifyAuth0MagicLink(
    email: string,
    code: string
  ): Promise<{ success: boolean; user?: Auth0User; tokens?: any; error?: string }> {
    try {
      const response = await this.post<Auth0MagicLinkVerifyResponse>(
        '/auth0/passwordless/email/verify',
        { email, code }
      );
      
      if (response.success && response.data) {
        // Store tokens
        this.setAuthTokens(
          response.data.accessToken,
          response.data.refreshToken || '',
          response.data.expiresIn
        );
        
        return {
          success: true,
          user: response.data.user,
          tokens: {
            accessToken: response.data.accessToken,
            idToken: response.data.idToken,
            refreshToken: response.data.refreshToken,
            expiresIn: response.data.expiresIn
          }
        };
      }
      
      return {
        success: false,
        error: response.error || 'Failed to verify magic link'
      };
    } catch (error) {
      console.error('❌ Failed to verify Auth0 magic link:', error);
      return {
        success: false,
        error: 'Failed to verify magic link'
      };
    }
  }

  // =============================================================================
  // AUTH0 SESSION MANAGEMENT
  // =============================================================================

  /**
   * Verify Auth0 session
   */
  async verifyAuth0Session(
    sessionToken: string,
    socketId?: string
  ): Promise<{ success: boolean; valid?: boolean; user?: Auth0User; error?: string }> {
    try {
      console.log('🔍 AuthService: Verifying session with token:', sessionToken ? sessionToken.substring(0, 20) + '...' : 'null');

      const response = await this.post<Auth0SessionVerifyResponse>(
        '/auth0/session/verify',
        { token: sessionToken },
        { retries: 0 } // Don't retry session validation - fail immediately
      );
      
      console.log('🔍 AuthService: Session verify response:', {
        success: response.success,
        dataValid: response.data?.valid,
        hasUser: !!response.data?.user,
        error: response.error
      });
      
      return {
        success: response.success,
        valid: response.data?.valid,
        user: response.data?.user,
        error: response.error || undefined
      };
    } catch (error) {
      console.error('❌ Failed to verify Auth0 session:', error);
      return {
        success: false,
        error: 'Failed to verify session'
      };
    }
  }

  // =============================================================================
  // LEGACY MAGIC LINK METHODS (for backward compatibility)
  // =============================================================================

  /**
   * Send magic link to guest email (legacy method)
   */
  async sendMagicLink(request: MagicLinkRequest): Promise<ApiResponse<MagicLinkResponse>> {
    // For backward compatibility, delegate to Auth0 magic link
    const result = await this.sendAuth0MagicLink(request.email, request.hotelId);
    
    if (result.success) {
      return {
        success: true,
        data: {
          success: true,
          message: 'Magic link sent successfully',
          linkId: `auth0_${Date.now()}`,
          expiresAt: new Date(Date.now() + 300000).toISOString() // 5 minutes
        },
        timestamp: new Date().toISOString()
      };
    }
    
    return {
      success: false,
      error: result.error || 'MAGIC_LINK_SEND_FAILED',
      message: result.error || 'Failed to send magic link',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Verify magic link and authenticate user (legacy method)
   */
  async verifyMagicLink(request: VerifyMagicLinkRequest): Promise<ApiResponse<AuthenticatedUser>> {
    // For backward compatibility, this would need to be implemented
    // based on the Auth0 magic link verification flow
    console.warn('Legacy verifyMagicLink called - implement Auth0 verification flow');
    
    return {
      success: false,
      error: 'LEGACY_METHOD_DEPRECATED',
      message: 'Use Auth0 verification methods instead',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Logout user and invalidate tokens
   */
  async logout(): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.post<{ success: boolean }>(
      '/auth/logout',
      {},
      { requireAuth: true }
    );

    // Clear tokens regardless of API response
    this.clearAuthTokens();
    return response;
  }

  /**
   * Validate current session (Auth0 integration)
   */
  async validateSession(sessionToken?: string): Promise<ApiResponse<{ valid: boolean; user?: Auth0User | GuestProfile }>> {
    console.log('Validating Auth0 session...');
    
    // Get session token from parameter or cookie
    const token = sessionToken || this.getSessionTokenFromCookie();
    
    if (!token) {
      return {
        success: false,
        error: 'NO_SESSION_TOKEN',
        message: 'No session token found',
        timestamp: new Date().toISOString()
      };
    }
    
    try {
      const result = await this.verifyAuth0Session(token);
      
      if (result.success && result.valid) {
        return {
          success: true,
          data: {
            valid: true,
            user: result.user
          },
          timestamp: new Date().toISOString()
        };
      }
      
      return {
        success: false,
        data: {
          valid: false
        },
        error: result.error || 'INVALID_SESSION',
        message: result.error || 'Session is invalid',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Session validation failed:', error);
      return {
        success: false,
        error: 'SESSION_VALIDATION_FAILED',
        message: 'Failed to validate session',
        timestamp: new Date().toISOString()
      };
    }
  }
  
  /**
   * Get session token from cookie
   */
  private getSessionTokenFromCookie(): string | null {
    const sessionCookie = document.cookie
      .split(';')
      .find(cookie => cookie.trim().startsWith('session_token='));
    return sessionCookie ? sessionCookie.split('=')[1].trim() : null;
  }

  /**
   * Get current user profile (Auth0 integration)
   */
  async getCurrentUser(): Promise<ApiResponse<Auth0User | GuestProfile>> {
    try {
      // First try to get Auth0 user info
      const sessionToken = this.getSessionTokenFromCookie();
      if (sessionToken) {
        const sessionResult = await this.verifyAuth0Session(sessionToken);
        if (sessionResult.success && sessionResult.user) {
          return {
            success: true,
            data: sessionResult.user,
            timestamp: new Date().toISOString()
          };
        }
      }
      
      // Fallback to legacy API
      return this.get<GuestProfile>('/api/auth/me', { requireAuth: true });
    } catch (error) {
      console.error('❌ Failed to get current user:', error);
      return {
        success: false,
        error: 'GET_USER_FAILED',
        message: 'Failed to get user information',
        timestamp: new Date().toISOString()
      };
    }
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Convert Auth0 user to legacy GuestProfile format
   */
  convertAuth0UserToGuestProfile(auth0User: Auth0User): GuestProfile {
    return {
      id: auth0User.sub,
      email: auth0User.email,
      fullName: auth0User.name,
      phone: auth0User.user_metadata?.phone,
      operaProfileId: auth0User.app_metadata?.operaProfileId,
      preferences: {
        language: auth0User.user_metadata?.language || 'en',
        currency: auth0User.user_metadata?.currency || 'USD',
        communicationPreference: auth0User.user_metadata?.communicationPreference || 'email'
      },
      journeyStage: auth0User.app_metadata?.journeyStage || 'prospect',
      currentReservation: auth0User.app_metadata?.currentReservation,
      lastStayDate: auth0User.app_metadata?.lastStayDate,
      isVIP: auth0User.app_metadata?.isVIP || false,
      loyaltyTier: auth0User.app_metadata?.loyaltyTier
    };
  }
  
  /**
   * Get recommended authentication method based on capabilities
   */
  async getRecommendedAuthMethod(): Promise<AuthMethod> {
    const capabilities = await this.getAuthCapabilities();
    
    // Prefer passkey if available
    if (capabilities.passkey.available) {
      return 'passkey';
    }
    
    // Then biometric if available
    if (capabilities.biometric.available) {
      return 'biometric';
    }
    
    // Fallback to magic link
    return 'magic_link';
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Convert base64url string to ArrayBuffer
   */
  private base64urlToArrayBuffer(base64url: string): ArrayBuffer {
    // Convert base64url to base64
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

}

// Export singleton instance
export const authService = new AuthService();

// Export Auth0 service instances for direct access if needed
export { auth0PasskeyService, auth0BiometricService };